import { useMemo, useState } from "react";
import { AlertBanner } from "@/components/admin/alert-banner";
import { AdminButton } from "@/components/admin/button";
import { Card, CardBody, CardHead } from "@/components/admin/card";
import { AdminIcon } from "@/components/admin/icon";
import { PageHead } from "@/components/admin/page-head";
import { Tag } from "@/components/admin/tag";
import { Modal, ModalFooter } from "@/components/ui/modal";
import {
	usePeriods,
	useVatBuild,
	useVatPreview,
	useVatReturns,
	useVatSubmit,
} from "@/lib/finance/finance-hooks";
import { fmtFCFA } from "@/lib/format";
import type { TagClass } from "@/lib/status";
import { apiError, Field, inputStyle } from "./_ui";

const MONTHS = [
	"janv.",
	"févr.",
	"mars",
	"avr.",
	"mai",
	"juin",
	"juil.",
	"août",
	"sept.",
	"oct.",
	"nov.",
	"déc.",
];
const STATUS_CLASS: Record<string, TagClass> = {
	DRAFT: "neutral",
	REVIEWED: "info",
	SUBMITTED: "success",
	PAID: "success",
};

/** FIN-07 — Déclarations fiscales TVA mensuelles. */
export function AdminVatPage() {
	const { data: periods } = usePeriods(2026);
	const monthly = useMemo(
		() => (periods ?? []).filter((p) => p.periodType === "MONTH"),
		[periods],
	);
	const [periodId, setPeriodId] = useState<string>("");
	const effectivePeriod = periodId || monthly[0]?.id || "";
	const { data: preview } = useVatPreview(effectivePeriod || undefined);
	const { data: returns } = useVatReturns();
	const build = useVatBuild();
	const submit = useVatSubmit();
	const [msg, setMsg] = useState<{
		kind: "danger" | "info";
		text: string;
	} | null>(null);

	const periodLabel = (id: string) => {
		const p = monthly.find((x) => x.id === id);
		return p
			? `${MONTHS[Number(p.startsOn.slice(5, 7)) - 1]} ${p.fiscalYear}`
			: id;
	};

	const doBuild = () => {
		setMsg(null);
		build.mutate(effectivePeriod, {
			onSuccess: () =>
				setMsg({ kind: "info", text: "Déclaration brouillon générée." }),
			onError: (e) => setMsg({ kind: "danger", text: apiError(e) }),
		});
	};
	const [submitId, setSubmitId] = useState<string | null>(null);
	const [receiptNo, setReceiptNo] = useState("");

	const openSubmit = (id: string) => {
		setReceiptNo("");
		setSubmitId(id);
	};
	const confirmSubmit = () => {
		if (!submitId) return;
		submit.mutate(
			{ id: submitId, dgiReceiptNo: receiptNo.trim() || undefined },
			{
				onSuccess: () => {
					setMsg({ kind: "info", text: "Déclaration soumise." });
					setSubmitId(null);
				},
				onError: (e) => setMsg({ kind: "danger", text: apiError(e) }),
			},
		);
	};

	return (
		<>
			<PageHead
				title="Déclarations TVA"
				sub="Mensuelle · échéance le 15 du mois suivant · taux 19,25 %"
			/>
			{msg && (
				<AlertBanner
					variant={msg.kind === "danger" ? "danger" : "info"}
					title={msg.text}
				/>
			)}

			<Card>
				<CardHead
					title="Pré-calcul de la période"
					right={
						<select
							style={{ ...inputStyle, width: 160 }}
							value={effectivePeriod}
							onChange={(e) => setPeriodId(e.target.value)}
						>
							{monthly.map((p) => (
								<option key={p.id} value={p.id}>
									{periodLabel(p.id)}
								</option>
							))}
						</select>
					}
				/>
				<CardBody>
					<div className="kpi-grid">
						<div className="kpi">
							<div className="kpi-l">TVA collectée (4431)</div>
							<div className="kpi-v tn">
								{fmtFCFA(preview?.vatCollected ?? 0)}
							</div>
						</div>
						<div className="kpi">
							<div className="kpi-l">TVA déductible B&S (4452)</div>
							<div className="kpi-v tn">
								{fmtFCFA(preview?.vatDeductibleBs ?? 0)}
							</div>
						</div>
						<div className="kpi">
							<div className="kpi-l">Crédit reporté M-1</div>
							<div className="kpi-v tn">
								{fmtFCFA(preview?.vatCreditPrev ?? 0)}
							</div>
						</div>
						<div
							className={`kpi ${(preview?.vatToPay ?? 0) >= 0 ? "accent" : "success"}`}
						>
							<div className="kpi-l">
								{(preview?.vatToPay ?? 0) >= 0 ? "Net à payer" : "Crédit TVA"}
							</div>
							<div className="kpi-v tn">
								{fmtFCFA(Math.abs(preview?.vatToPay ?? 0))}
							</div>
							<div className="kpi-foot muted">
								échéance {preview?.filingDueAt ?? "—"}
							</div>
						</div>
					</div>
					<div
						style={{
							display: "flex",
							justifyContent: "flex-end",
							marginTop: 12,
						}}
					>
						<AdminButton
							variant="primary"
							onClick={doBuild}
							disabled={!effectivePeriod || build.isPending}
						>
							<AdminIcon n="check" s={14} /> Générer le brouillon
						</AdminButton>
					</div>
				</CardBody>
			</Card>

			<Card style={{ marginTop: 16 }}>
				<CardHead title="Historique des déclarations" />
				<CardBody>
					<div className="table-wrap" style={{ margin: 0 }}>
						<table className="table">
							<thead>
								<tr>
									<th>Période</th>
									<th>Statut</th>
									<th className="right">Collectée</th>
									<th className="right">Déductible</th>
									<th className="right">Net à payer</th>
									<th>Échéance</th>
									<th>Récépissé</th>
									<th />
								</tr>
							</thead>
							<tbody>
								{(returns ?? []).length === 0 && (
									<tr>
										<td
											colSpan={8}
											className="muted"
											style={{ textAlign: "center", padding: 24 }}
										>
											Aucune déclaration.
										</td>
									</tr>
								)}
								{(returns ?? []).map((r) => (
									<tr key={r.id}>
										<td>{periodLabel(r.taxPeriodId)}</td>
										<td>
											<Tag variant={STATUS_CLASS[r.status] ?? "neutral"}>
												{r.status}
											</Tag>
										</td>
										<td className="right tn">{fmtFCFA(r.vatCollected)}</td>
										<td className="right tn">
											{fmtFCFA(r.vatDeductibleBs + r.vatDeductibleImmo)}
										</td>
										<td className="right tn">
											<strong>{fmtFCFA(r.vatToPay)}</strong>
										</td>
										<td className="muted" style={{ fontSize: 12 }}>
											{r.filingDueAt}
										</td>
										<td className="mono" style={{ fontSize: 11 }}>
											{r.dgiReceiptNo ?? "—"}
										</td>
										<td>
											{r.status === "DRAFT" && (
												<AdminButton
													variant="ghost"
													size="sm"
													onClick={() => openSubmit(r.id)}
												>
													Soumettre
												</AdminButton>
											)}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</CardBody>
			</Card>

			{submitId && (
				<Modal
					title="Soumettre la déclaration TVA"
					subtitle="La déclaration passera au statut « soumise ». Renseignez le récépissé du téléportail DGI si vous l'avez déjà."
					onClose={() => setSubmitId(null)}
				>
					<form
						onSubmit={(ev) => {
							ev.preventDefault();
							confirmSubmit();
						}}
					>
						<Field label="N° de récépissé DGI (optionnel)">
							<input
								autoFocus
								style={inputStyle}
								value={receiptNo}
								onChange={(ev) => setReceiptNo(ev.target.value)}
								maxLength={60}
								placeholder="Laisser vide si non encore transmis"
							/>
						</Field>
						<ModalFooter>
							<button
								type="button"
								className="btn btn-ghost"
								onClick={() => setSubmitId(null)}
							>
								Annuler
							</button>
							<button
								type="submit"
								className="btn btn-primary"
								disabled={submit.isPending}
								style={{ opacity: submit.isPending ? 0.5 : 1 }}
							>
								{submit.isPending ? "Soumission…" : "Soumettre"}
							</button>
						</ModalFooter>
					</form>
				</Modal>
			)}
		</>
	);
}
